import { PositionConfig } from "slotworks/config/asset-config-schema";
import AnimationManager from "slotworks/graphics/animation/animation-manager";
import { SmartShape } from "slotworks/graphics/elements/smart-shape";
import GraphicsManager from "slotworks/graphics/graphics-manager";
import Layers from "slotworks/graphics/layers/layers";
import { generateButton } from "slotworks/graphics/layers/populators/generate-button";
import { generateToggle } from "slotworks/graphics/layers/populators/generate-toggle";
import ParticleManager from "slotworks/graphics/particles/particle-manager";
import Point from "slotworks/utils/geom/point";
import Rectangle from "slotworks/utils/geom/rectangle";

export function populateSprites(sprites: PositionConfig[], layer: Layers) {
    if (sprites) {
        for (const graphicConfig of sprites) {
            const id = graphicConfig.id;
            const name = id.substring(id.lastIndexOf("/") + 1);

            if (name.slice(0, 4) === "btn-") {
                // Buttons
                const buttonMetaData = name.substring(4).split("-");
                const buttonName = buttonMetaData[0];

                if (buttonMetaData.length === 1 || buttonMetaData[1] === "up") {
                    generateButton(buttonName, graphicConfig, layer);

                    const otherButtonStates = sprites.filter((value) => value !== graphicConfig && value.id.indexOf("btn-" + buttonName) > -1);
                    for (const otherButtonState of otherButtonStates) {
                        let buttonPath = otherButtonState.id.substring(otherButtonState.id.lastIndexOf("/") + 1);
                        buttonPath = buttonPath.substr(4);
                        generateButton(buttonPath, otherButtonState, layer);
                    }
                }
            } else if (name.slice(0, 9) === "particle-") {
                // particles
                const particleInfo = name.split("-");
                const particleId = particleInfo[1];
                const particleInstanceName = particleInfo.length > 2 ? particleInfo[2] : particleId;

                const particleContainer = new PIXI.Container();
                const particle = ParticleManager.add(particleId, new Point(graphicConfig.x, graphicConfig.y), particleContainer);

                layer.addParticle(particleInstanceName, particle);
                layer.add(particleContainer, false, true, particleId);
            } else if (name.slice(0, 5) === "anim-") {
                // Animations
                const animOptions = name.split("-");

                const animationId = animOptions[1];
                let instanceId = animationId;
                let autoplay = true;

                if (animOptions.length > 2) {
                    instanceId = animOptions[2];
                    if (instanceId === "true" || instanceId === "false") {
                        autoplay = instanceId === "true";
                        instanceId = animationId;
                    }
                }
                if (animOptions.length > 3) {
                    autoplay = animOptions[3] === "true";
                }

                if (AnimationManager.hasScriptedAnimationFactory(animationId)) {
                    const animPosition = new Rectangle(graphicConfig.x, graphicConfig.y, graphicConfig.width, graphicConfig.height);
                    const anim = AnimationManager.createScriptedAnimation(animationId, animPosition);

                    layer.addScriptedAnimation(instanceId, anim);

                    if (autoplay) {
                        anim.play();
                    } else {
                        anim.getContainer().visible = false;
                    }

                    layer.add(anim.getContainer(), false, true, animationId);

                } else {
                    const anim = GraphicsManager.createAnimation(animationId);
                    anim.x = graphicConfig.x;
                    anim.y = graphicConfig.y;
                    anim.width = graphicConfig.width;
                    anim.height = graphicConfig.height;
                    anim.name = instanceId;

                    if (autoplay) {
                        anim.play();
                    } else {
                        anim.visible = false;
                    }

                    layer.add(anim, false, true, anim.name);
                }
            } else if (name.slice(0, 6) === "spine-") {
                // Animations
                const spineOptions = name.split("-");

                const spineId = spineOptions[1];
                let instanceId = spineId;
                let animationName;
                let loopAnimation = false;

                if (spineOptions.length > 2) {
                    instanceId = spineOptions[2];
                }
                if (spineOptions.length > 3) {
                    animationName = spineOptions[3];
                }
                if (spineOptions.length > 4) {
                    loopAnimation = spineOptions[4] === "true";
                }

                const spine = GraphicsManager.createSpine(spineId);

                const bounds = spine.getBounds();
                const offset = new Point(spine.x - bounds.x, spine.y - bounds.y);

                spine.width = graphicConfig.width;
                spine.height = graphicConfig.height;

                spine.pivot.x = -offset.x;
                spine.pivot.y = -offset.y;
                spine.x = graphicConfig.x;
                spine.y = graphicConfig.y;

                const bestFitScale = Math.min(spine.scale.x, spine.scale.y);
                spine.scale.set(bestFitScale, bestFitScale);

                spine.name = instanceId;

                if (animationName) {
                    spine.state.setAnimation(0, animationName, loopAnimation);
                }

                layer.add(spine, false, true, instanceId);
            } else if (name.slice(0, 6) === "shape-") {
                // Shapes
                const shapeParams = name.slice(6).split("-");

                const shape = new SmartShape();
                shape.name = shapeParams[0];

                shape.shapeFillColor = parseInt(shapeParams[1], 16);
                shape.shapeFillAlpha = shapeParams.length > 2 ? parseFloat(shapeParams[2]) / 100 : 1;
                shape.shapeStrokeColor = shapeParams.length > 3 ? parseInt(shapeParams[3], 16) : shape.shapeFillColor;
                shape.shapeStrokeSize = shapeParams.length > 4 ? parseFloat(shapeParams[4]) : 0;
                shape.shapeStrokeAlpha = shapeParams.length > 5 ? parseFloat(shapeParams[5]) / 100 : shape.shapeFillAlpha;
                shape.shapeCornerRadius = shapeParams.length > 6 ? parseFloat(shapeParams[6]) : 0;

                shape.x = graphicConfig.x;
                shape.y = graphicConfig.y;
                shape.shapeWidth = graphicConfig.width;
                shape.shapeHeight = graphicConfig.height;

                shape.draw();

                layer.add(shape, false, true, shape.name);
            } else if (name.slice(0, 4) === "tgl-") {
                // Toggle buttons
                generateToggle(name.substring(4), graphicConfig, layer);
            } else {
                // Sprites
                const sprite = GraphicsManager.createSprite(id);
                sprite.x = graphicConfig.x;
                sprite.y = graphicConfig.y;
                sprite.width = graphicConfig.width;
                sprite.height = graphicConfig.height;
                sprite.name = name;

                layer.add(sprite, false, true, sprite.name);
            }
        }
    }
}
